import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { StepperComponent } from "../../../../_start/assets/ts/components";
import { KTSVG } from "../../../../_start/helpers";
import { IProjectDetailsData, defaultProjectDetailsData } from "./ProjectDetailsModel";
import { db, authFirebase } from "../../../../config/firebase";
import { doc, collection, setDoc, getDocs, getDoc, addDoc, updateDoc } from "firebase/firestore";
import { TProjectMotivation } from "../_modals/create-app-stepper/IAppModels";

interface Props {
  userID: string;
  projectTitle: string;
  projectID: string;
  projectDescription?: string;
  projectMotivation?: string;
}

const ProjectDetailsPage: React.FC<Props> = ({ userID, projectTitle, projectID, projectDescription = "", projectMotivation = "" }) => {
  const navigate = useNavigate();
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);
  const [data, setData] = useState<IProjectDetailsData>(defaultProjectDetailsData);
  const [hasError, setHasError] = useState(false);
  const [useAI, setUseAI] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(
      stepperRef.current as HTMLDivElement
    );
  };

  const updateData = useCallback((fieldsToUpdate: Partial<IProjectDetailsData>) => {
    setData(prevData => ({ ...prevData, ...fieldsToUpdate }));
  }, []);

  const checkProjectName = (): boolean => {
    if (!data.projectName) {
      return false;
    }
    return true;
  };

  const checkProjectMotivation = (): boolean => {
    if (!data.projectMotivation) {
      return false;
    }

    return true;
  };

  const prevStep = () => {
    if (!stepper.current) {
      return;
    }

    stepper.current.goPrev();
  };

  const nextStep = () => {
    setHasError(false);
    if (!stepper.current) {
      return;
    }

    if (stepper.current.getCurrentStepIndex() === 1) {
      if (!checkProjectName()) {
        setHasError(true);
        return;
      }
    }

    if (stepper.current.getCurrentStepIndex() === 3) {
      if (!checkProjectMotivation()) {
        setHasError(true);
        return;
      }
    }

    stepper.current.goNext();
  };
  
  const writeData = async (userID: string) => {

    const userRef = doc(db, "users", userID);
      // Define the collection and document data
    const projectsRef = collection(userRef, "projects");
    const projectDoc = doc(projectsRef, projectID);
    const projectData = {
      projectTitle: data.projectName,
      projectProgress: 0,
      projectDescription: data.projectDescription,
      projectMotivation: data.projectMotivation,
    }
    // Add a new document with a generated id.
    await updateDoc(projectDoc, projectData);

    return;
  }

  // const fetchUser = async () => {
  //   const userID = await store.getState().auth.user.uid;

  //   return userID;
  // }

  const submit = async (userID: string) => {
    setIsSubmitting(true);
    try {
      await writeData(userID);

      // If AI is enabled and description is long enough, navigate to AI progress
      if (useAI && data.projectDescription.trim().length >= 50) {
        navigate("/ai-progress", {
          state: {
            userID,
            projectID,
            projectName: data.projectName,
            projectDescription: data.projectDescription,
          },
        });
      } else {
        // Otherwise go back to project dashboard
        navigate("/project", {
          state: {
            userID,
            projectID,
            projectTitle: data.projectName,
            projectDescription: data.projectDescription,
          },
        });
      }
    } catch (error) {
      console.error("Failed to update project:", error);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {

    updateData({
      projectName: projectTitle,
      projectDescription: projectDescription,
      projectMotivation: projectMotivation as TProjectMotivation,
    })

    if (!stepperRef.current) {
      return;
    }

    loadStepper();
  }, [updateData, projectTitle, projectDescription, projectMotivation]);

  return (
    <div className="card card-custom">
      {/* begin::Card Body */}
      <div className="card-body p-10 p-lg-15 p-xxl-30">
          {/*begin::Stepper */}
          <div
            ref={stepperRef}
            className="stepper stepper-1 d-flex flex-column flex-xl-row flex-row-fluid"
            id="kt_modal_create_app_stepper"
          >
            {/*begin::Aside */}
            <div className="d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px">
              {/*begin::Nav */}
              <div className="stepper-nav d-flex flex-column pt-5">
                {/*begin::Step 1 */}
                <div
                  className="stepper-item current"
                  data-kt-stepper-element="nav"
                >
                  <div className="stepper-wrapper">
                    <div className="stepper-icon">
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">1</span>
                    </div>
                    <div className="stepper-label">
                      <h3 className="stepper-title">New Project</h3>
                      <div className="stepper-desc">Name your Business Idea</div>
                    </div>
                  </div>
                </div>
                {/*end::Step 1 */}

                {/*begin::Step 2 */}
                <div className="stepper-item" data-kt-stepper-element="nav">
                  <div className="stepper-wrapper">
                    <div className="stepper-icon">
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">2</span>
                    </div>
                    <div className="stepper-label">
                      <h3 className="stepper-title">Project Description</h3>
                      <div className="stepper-desc">
                        Describe your idea
                      </div>
                    </div>
                  </div>
                </div>
                {/*end::Step 2 */}

                {/*begin::Step 3 */}
                <div className="stepper-item" data-kt-stepper-element="nav">
                  <div className="stepper-wrapper">
                    <div className="stepper-icon">
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">3</span>
                    </div>
                    <div className="stepper-label">
                      <h3 className="stepper-title">Motivation</h3>
                      <div className="stepper-desc">
                        What is your motivation for this project
                      </div>
                    </div>
                  </div>
                </div>
                {/*end::Step 3 */}

                {/*begin::Step 4 */}
                <div className="stepper-item" data-kt-stepper-element="nav">
                  <div className="stepper-wrapper">
                    <div className="stepper-icon">
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">4</span>
                    </div>
                    <div className="stepper-label">
                      <h3 className="stepper-title">Complete!</h3>
                      <div className="stepper-desc">Review and Create</div>
                    </div>
                  </div>
                </div>
                {/*end::Step 4 */}
              </div>
              {/*end::Nav */}
            </div>
            {/*begin::Aside */}

            {/*begin::Content */}
            <div className="d-flex flex-row-fluid justify-content-center">
              {/*begin::Form */}
              <form
                className="pb-5 w-100 w-md-400px w-xl-500px"
                noValidate
                id="kt_modal_create_app_form"
              >
                {/*begin::Step 1 */}
                <div className="pb-5 current" data-kt-stepper-element="content">
                  <div className="w-100">
                    {/*begin::Heading */}
                    <div className="pb-5 pb-lg-10">
                      <h3 className="fw-bolder text-dark display-6">
                        New Project
                      </h3>
                    </div>
                    {/*begin::Heading */}

                    {/*begin::Form Group */}
                    <div className="fv-row mb-12">
                      <label className="fs-6 fw-bolder text-dark form-label">
                        Give Your Business Idea a Name {projectTitle}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg form-control-solid"
                        name="appname"
                        placeholder=""
                        value={data.projectName}
                        onChange={(e) =>
                          updateData({
                            projectName: e.target.value,
                          })
                        }
                      />
                      <span>Don't worry, you can change it anytime later.</span>
                      {!data.projectName && hasError && (
                        <div className="fv-plugins-message-container">
                          <div
                            data-field="appname"
                            data-validator="notEmpty"
                            className="fv-help-block"
                          >
                            Project name is required
                          </div>
                        </div>
                      )}
                    </div>
                    {/*end::Form Group */}

                    {/* begin::Form Group */}
                    <div className="fv-row">
                      {/*begin:Option */}
                      {/* <label className="d-flex align-items-center justify-content-between mb-6 cursor-pointer">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-primary">
                              <KTSVG
                                path="/media/icons/duotone/Home/Globe.svg"
                                className="svg-icon-1 svg-icon-primary"
                              />
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">
                              Quick Online Courses
                            </span>
                            <span className="fs-7 text-muted">
                              Creating a clear text structure is just one SEO
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="appType"
                            value="Quick Online Courses"
                            checked={
                              data.appBasic.appType === "Quick Online Courses"
                            }
                            onChange={() =>
                              updateData({
                                appBasic: {
                                  appName: data.appBasic.appName,
                                  appType: "Quick Online Courses",
                                },
                              })
                            }
                          />
                        </span>
                      </label> */}
                      {/*end::Option */}

                      {/*begin:Option */}
                      {/* <label className="d-flex align-items-center justify-content-between mb-6 cursor-pointer">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-danger">
                              <KTSVG
                                path="/media/icons/duotone/Layout/Layout-4-blocks-2.svg"
                                className="svg-icon-1 svg-icon-danger"
                              />
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">
                              Face to Face Discussions
                            </span>
                            <span className="fs-7 text-muted">
                              Creating a clear text structure is just one aspect
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="appType"
                            value="Face to Face Discussions"
                            checked={
                              data.appBasic.appType ===
                              "Face to Face Discussions"
                            }
                            onChange={() =>
                              updateData({
                                appBasic: {
                                  appName: data.appBasic.appName,
                                  appType: "Face to Face Discussions",
                                },
                              })
                            }
                          />
                        </span>
                      </label> */}
                      {/*end::Option */}

                      {/*begin:Option */}
                      {/* <label className="d-flex align-items-center justify-content-between mb-6 cursor-pointer">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-success">
                              <KTSVG
                                path="/media/icons/duotone/Devices/Watch1.svg"
                                className="svg-icon-1 svg-icon-success"
                              />
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">
                              Full Intro Training
                            </span>
                            <span className="fs-7 text-muted">
                              Creating a clear text structure copywriting
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="appType"
                            value="Full Intro Training"
                            checked={
                              data.appBasic.appType === "Full Intro Training"
                            }
                            onChange={() =>
                              updateData({
                                appBasic: {
                                  appName: data.appBasic.appName,
                                  appType: "Full Intro Training",
                                },
                              })
                            }
                          />
                        </span>
                      </label> */}
                      {/*end::Option */}
                    </div>
                    {/*end::Form Group */}
                  </div>
                </div>
                {/*end::Step 1 */}

                {/*begin::Step 2 */}
                <div className="pb-5" data-kt-stepper-element="content">
                  <div className="w-100">
                    {/*begin::Heading */}
                    <div className="pb-5 pb-lg-10">
                      <h3 className="fw-bolder text-dark display-6">
                        Describe Your Business Idea
                      </h3>
                      <p className="text-muted fs-6 mt-3">
                        Tell us about your business in your own words. Our AI will analyze your idea
                        and automatically fill in the 24-step validation framework.
                      </p>
                    </div>
                    {/*end::Heading */}

                    {/*begin::Form Group */}
                    <div className="fv-row">
                      <label className="fs-6 fw-bolder text-dark mb-3">
                        Business Description
                      </label>
                      <p className="text-muted fs-7 mb-4">
                        Include details about: what problem you solve, who your customers are,
                        how you make money, and what makes you different.
                      </p>

                      <textarea
                        className="form-control form-control-lg form-control-solid"
                        name="projectDescription"
                        rows={8}
                        placeholder="Example: I want to build a subscription box service for pet owners that delivers healthy, organic treats and eco-friendly toys monthly. We target urban millennials (25-40) who treat their pets like family and are willing to pay premium prices for quality products."
                        value={data.projectDescription}
                        onChange={(e) =>
                          updateData({ projectDescription: e.target.value })
                        }
                      />
                      <div className="d-flex justify-content-between mt-2">
                        <span className="text-muted fs-7">
                          {data.projectDescription.length} characters
                          {data.projectDescription.length < 100 && " (minimum 100 recommended)"}
                        </span>
                        {data.projectDescription.length >= 100 && (
                          <span className="text-success fs-7">
                            <i className="bi bi-check-circle-fill text-success me-1"></i>
                            Ready for AI analysis
                          </span>
                        )}
                      </div>
                    </div>
                    {/*end::Form Group */}

                    {/*begin::AI Toggle */}
                    <div className="fv-row mt-8">
                      <label className="d-flex align-items-center justify-content-between cursor-pointer">
                        <span className="d-flex flex-column">
                          <span className="fw-bolder fs-6 text-dark">
                            <i className="bi bi-magic text-primary me-2"></i>
                            AI-Powered Analysis
                          </span>
                          <span className="fs-7 text-muted">
                            Re-analyze and update the 24-step validation framework
                          </span>
                        </span>
                        <span className="form-check form-switch form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={useAI}
                            onChange={(e) => setUseAI(e.target.checked)}
                          />
                        </span>
                      </label>
                    </div>
                    {/*end::AI Toggle */}
                  </div>
                </div>
                {/*end::Step 2 */}

                {/*begin::Step 3 */}
                <div className="pb-5" data-kt-stepper-element="content">
                  <div className="w-100">
                    {/*begin::Heading */}
                    <div className="pb-10 pb-lg-15">
                      <h3 className="fw-bolder text-dark display-6">
                        Motivation
                      </h3>
                      <p>To get started with this process, you just need an idea, a technology or an inspiration. But let’s be clear which one of these you are starting with and put a little more definition on "it".</p>
                    </div>
                    {/*begin::Heading */}

                    {/*begin::Form Group */}
                    {/* <div className="fv-row mb-12">
                      <label className="fs-6 fw-bolder text-dark form-label">
                        App Databse Name Name
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg form-control-solid"
                        name="dbname"
                        value={data.appDatabase.databaseName}
                        onChange={(e) =>
                          updateData({
                            appDatabase: {
                              databaseName: e.target.value,
                              databaseSolution:
                                data.appDatabase.databaseSolution,
                            },
                          })
                        }
                      />
                      {!data.appDatabase.databaseName && hasError && (
                        <div className="fv-plugins-message-container">
                          <div
                            data-field="appname"
                            data-validator="notEmpty"
                            className="fv-help-block"
                          >
                            Database name is required
                          </div>
                        </div>
                      )}
                    </div> */}
                    {/*end::Form Group */}

                    {/*begin::Form Group */}
                    <div className="fv-row">
                      {/* <label className="fs-6 fw-bolder text-dark mb-7">
                        Select your app database solution
                      </label> */}

                      {/*begin:Option */}
                      <label className="d-flex align-items-center justify-content-between cursor-pointer mb-6">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-success">
                              <i className="fas fa-database text-success fs-2x"></i>
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">I have an idea</span>
                            <span className="fs-7 text-muted">
                              What is the idea? What generated the idea? What makes it unique and better than the current alternatives? Who do you think it ​would​ be relevant to? Why are you the one who should bring this idea to market?
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="motivation"
                            value="Idea"
                            checked={
                              data.projectMotivation === "Idea"
                            }
                            onChange={(e) => {
                                                            updateData({ projectMotivation: e.target.value as TProjectMotivation });
                            }}
                          />
                        </span>
                      </label>
                      {/*end::Option */}

                      {/*begin:Option */}
                      <label className="d-flex align-items-center justify-content-between cursor-pointer mb-6">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-danger">
                              <i className="fab fa-google text-danger fs-2x"></i>
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">I have a technology</span>
                            <span className="fs-7 text-muted">
                              What is the technology? Where did it come from? What makes it unique and better than the current alternatives? Who do you think it would be relevant to? Why are you the one to bring it to market?
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="motivation"
                            value="Technology"
                            checked={
                              data.projectMotivation === "Technology"
                            }
                            onChange={(e) => {
                                                            updateData({ projectMotivation: e.target.value as TProjectMotivation });
                            }}
                          />
                        </span>
                      </label>
                      {/*end::Option */}

                      {/*begin:Option */}
                      <label className="d-flex align-items-center justify-content-between cursor-pointer mb-6">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-6">
                            <span className="symbol-label bg-light-warning">
                              <i className="fab fa-amazon text-warning fs-2x"></i>
                            </span>
                          </span>

                          <span className="d-flex flex-column">
                            <span className="fw-bolder fs-6">I have inspiration</span>
                            <span className="fs-7 text-muted">
                              Inspiration is crucial as every entrepreneur needs it. You want to be an entrepreneur but you have toget either an idea or a technology or a co-founder who can help you zero in on one of these two things.
                            </span>
                          </span>
                        </span>

                        <span className="form-check form-check-custom form-check-solid">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="motivation"
                            value="Inspiration"
                            checked={
                              data.projectMotivation === "Inspiration"
                            }
                          onChange={(e) => {
                                                        updateData({ projectMotivation: e.target.value as TProjectMotivation });
                          }}
                          />
                        </span>
                      </label>
                      {/*end::Option */}
                    </div>
                    {/*end::Form Group */}
                  </div>
                </div>
                {/*end::Step 3 */}

                {/*begin::Step 4 */}
                <div className="pb-5" data-kt-stepper-element="content">
                  <div className="w-100">
                    {/* begin::Heading */}
                    <div className="pb-10 pb-lg-15">
                      <h3 className="fw-bolder text-dark display-6">
                        Ready to Update
                      </h3>
                      <div className="text-muted fw-bold fs-5">
                        Review your changes {useAI && data.projectDescription.length >= 50 && "and let AI re-analyze your business idea"}
                      </div>
                    </div>
                    {/* end::Heading */}

                    {/* begin::Section */}
                    <div className="d-flex align-items-center mb-6">
                      <div className="symbol symbol-40px me-4">
                        <div className="symbol-label bg-light-primary">
                          <i className="bi bi-briefcase text-primary fs-4"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="fw-bolder mb-0">{data.projectName}</h4>
                        <span className="text-muted fs-7">Project Name</span>
                      </div>
                    </div>
                    {/* end::Section */}

                    {/* begin::Section */}
                    <div className="d-flex align-items-start mb-6">
                      <div className="symbol symbol-40px me-4">
                        <div className="symbol-label bg-light-info">
                          <i className="bi bi-file-text text-info fs-4"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="fw-bolder mb-1">Description</h4>
                        <p className="text-gray-600 fs-7 mb-0" style={{ maxHeight: "100px", overflow: "auto" }}>
                          {data.projectDescription || "No description provided"}
                        </p>
                      </div>
                    </div>
                    {/* end::Section */}

                    {/* begin::Section */}
                    <div className="d-flex align-items-center mb-6">
                      <div className="symbol symbol-40px me-4">
                        <div className="symbol-label bg-light-warning">
                          <i className="bi bi-lightbulb text-warning fs-4"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="fw-bolder mb-0">{data.projectMotivation}</h4>
                        <span className="text-muted fs-7">Motivation</span>
                      </div>
                    </div>
                    {/* end::Section */}

                    {/* begin::AI Info */}
                    {useAI && data.projectDescription.length >= 50 && (
                      <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mt-6">
                        <i className="bi bi-magic fs-2tx text-primary me-4"></i>
                        <div className="d-flex flex-stack flex-grow-1">
                          <div className="fw-bold">
                            <h4 className="text-gray-800 fw-bolder">AI Analysis Enabled</h4>
                            <div className="fs-6 text-gray-600">
                              When you submit, AI will re-analyze your business idea and
                              update the 24-step validation framework.
                              You can review and edit each step afterwards.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* end::AI Info */}

                    {/* begin::Submitting Progress */}
                    {isSubmitting && (
                      <div className="mt-8">
                        <div className="d-flex align-items-center mb-3">
                          <Spinner animation="border" size="sm" className="me-3 text-primary" />
                          <span className="fw-bold text-gray-700">
                            Updating your project...
                          </span>
                        </div>
                      </div>
                    )}
                    {/* end::Submitting Progress */}
                  </div>
                </div>
                {/*end::Step 4 */}

                {/*begin::Actions */}
                <div className="d-flex justify-content-between pt-10">
                  <div className="mr-2">
                    <button
                      type="button"
                      className="btn btn-lg btn-light-primary fw-bolder py-4 pe-8 me-3"
                      data-kt-stepper-action="previous"
                      onClick={prevStep}
                      disabled={isSubmitting}
                    >
                      <KTSVG
                        path="/media/icons/duotone/Navigation/Left-2.svg"
                        className="svg-icon-3 me-1"
                      />{" "}
                      Previous
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                      data-kt-stepper-action="submit"
                      onClick={() => submit(userID)}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          {useAI && data.projectDescription.length >= 50 ? (
                            <>
                              <i className="bi bi-magic me-2"></i>
                              Update with AI
                            </>
                          ) : (
                            <>
                              Save Changes
                              <KTSVG
                                path="/media/icons/duotone/Navigation/Right-2.svg"
                                className="svg-icon-3 ms-2"
                              />
                            </>
                          )}
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                      data-kt-stepper-action="next"
                      onClick={nextStep}
                      disabled={isSubmitting}
                    >
                      Next Step{" "}
                      <KTSVG
                        path="/media/icons/duotone/Navigation/Right-2.svg"
                        className="svg-icon-3 ms-1"
                      />
                    </button>
                  </div>
                </div>
                {/*end::Actions */}
              </form>
              {/*end::Form */}
            </div>
            {/*end::Content */}
          </div>
          {/* end::Stepper */}
      </div>
      {/* end::Card Body */}
    </div>
  );
}

export { ProjectDetailsPage };
